import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  CreateGroupDto,
  GroupDto,
  ListGroupResponseDto,
  ListGroupsOfPartitionDto,
  ListMemberResponseDto,
  MemberRole,
  MemberSearchResponse,
  MemberType,
  MembersCountResponseDto,
  UpdateGroupOperation,
  UpdateGroupResponseDto,
  VersionInfo
} from '../types/entitlements';
import { getCurrentEnvironment, getBackendEnvironmentName } from '../config/environments';

// Get OSDU token from auth context
// This function will be set by the API instance. Providers may be async so the
// token can be acquired silently (and refreshed if expired) on each request.
let getOsduTokenFn: (() => string | null | Promise<string | null>) | null = null;
let getGraphTokenFn: (() => string | null | Promise<string | null>) | null = null;

export const setOsduTokenProvider = (fn: () => string | null | Promise<string | null>) => {
  getOsduTokenFn = fn;
};

export const setGraphTokenProvider = (fn: () => string | null | Promise<string | null>) => {
  getGraphTokenFn = fn;
};

// Create axios instance with default config
const createApiClient = (baseURL: string): AxiosInstance => {
  const config: AxiosRequestConfig = {
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const client = axios.create(config);

  // Request interceptor to add authorization and environment information
  client.interceptors.request.use(
    async (config) => {
      // Add environment information for backend server
      const currentEnv = getCurrentEnvironment();
      const backendEnv = getBackendEnvironmentName(currentEnv);

      config.headers['x-environment'] = backendEnv;

      // Add Authorization header with OSDU token.
      // The provider acquires the token silently (refreshing it if it has
      // expired, e.g. after a period of inactivity) so requests never go out
      // with a stale/missing token.
      if (getOsduTokenFn) {
        const osduToken = await getOsduTokenFn();
        if (osduToken) {
          config.headers['Authorization'] = `Bearer ${osduToken}`;
        }
      }

      // Add Graph token header for app registration display name resolution
      if (getGraphTokenFn) {
        const graphToken = await getGraphTokenFn();
        if (graphToken) {
          config.headers['x-graph-token'] = graphToken;
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle errors
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Handle unauthorized - could redirect to login
        localStorage.removeItem('authToken');
        // Don't redirect here since MSAL will handle auth
        console.error('Unauthorized request');
      }
      return Promise.reject(error);
    }
  );

  return client;
};

export class EntitlementsApi {
  private client: AxiosInstance;

  constructor(baseURL: string = '/api') {
    this.client = createApiClient(baseURL);
  }

  // Health checks
  async livenessCheck() {
    return this.client.get('/health');
  }

  async readinessCheck() {
    return this.client.get('/health');
  }

  // Groups API with pagination support
  async listGroups(params?: { 
    onBehalfOf?: string; 
    roleRequired?: boolean;
    limit?: number;
    offset?: number;
    cursor?: string;
  }) {
    const headers: any = {};
    if (params?.onBehalfOf) {
      headers['on-behalf-of'] = params.onBehalfOf;
    }
    
    const queryParams: any = {
      roleRequired: params?.roleRequired || false
    };
    
    // Add pagination params if provided
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.offset !== undefined) queryParams.offset = params.offset;
    if (params?.cursor) queryParams.cursor = params.cursor;
    
    return this.client.get<ListGroupResponseDto>('/groups', {
      headers,
      params: queryParams
    });
  }

  async createGroup(data: CreateGroupDto) {
    return this.client.post<GroupDto>('/groups', data);
  }

  // async deleteGroup(groupEmail: string) {
  //   return this.client.delete(`/groups/${encodeURIComponent(groupEmail)}`);
  // }

  async updateGroup(groupEmail: string, operations: UpdateGroupOperation[]) {
    return this.client.patch<UpdateGroupResponseDto>(
      `/groups/${encodeURIComponent(groupEmail)}`,
      operations
    );
  }

  // Members API with pagination support
  async listGroupMembers(
    groupEmail: string, 
    params?: { 
      role?: MemberRole; 
      includeType?: boolean;
      limit?: number;
      offset?: number;
      cursor?: string;
    }
  ) {
    return this.client.get<ListMemberResponseDto>(
      `/groups/${encodeURIComponent(groupEmail)}/members`,
      { params }
    );
  }

  async removeMember(groupEmail: string, memberEmail: string) {
    return this.client.delete(
      `/groups/${encodeURIComponent(groupEmail)}/members/${encodeURIComponent(memberEmail)}`
    );
  }

  async deleteMember(memberEmail: string) {
    return this.client.delete(`/members/${encodeURIComponent(memberEmail)}`);
  }

  async listGroupsOnBehalfOf(
    memberEmail: string,
    type: MemberType,
    params?: { 
      appid?: string; 
      roleRequired?: boolean;
      limit?: number;
      offset?: number;
      cursor?: string;
    }
  ) {
    return this.client.get<ListGroupResponseDto>(
      `/members/${encodeURIComponent(memberEmail)}/groups`,
      {
        params: { type, ...params }
      }
    );
  }

  async countGroupMembers(groupEmail: string, role?: MemberRole) {
    return this.client.get<MembersCountResponseDto>(
      `/groups/${encodeURIComponent(groupEmail)}/membersCount`,
      { params: { role } }
    );
  }

  // Tenant provisioning
  async initiateTenant() {
    return this.client.post('/tenant-provisioning');
  }

  // Partition groups with pagination
  async listAllPartitionGroups(type: MemberType, params?: { cursor?: string; limit?: number }) {
    return this.client.get<ListGroupsOfPartitionDto>('/groups/all', {
      params: { type, ...params }
    });
  }

  // Version info
  async getVersionInfo() {
    return this.client.get<VersionInfo>('/info');
  }

  // Graph API search - Note: This uses a different endpoint (not OSDU)
  async searchMembersViaGraph(query: string) {
    const currentEnv = getCurrentEnvironment();
    const backendEnv = getBackendEnvironmentName(currentEnv);

    return axios.get<MemberSearchResponse>(`/graph/search/members`, {
      params: { q: query },
      headers: {
        'x-environment': backendEnv
      }
    });
  }

  // Generic HTTP methods - Use these for any endpoint not covered by specific methods above
  // These methods automatically include authentication headers via interceptors
  async get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.put<T>(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.patch<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T>(url, config);
  }
}

// Export singleton instance
export const api = new EntitlementsApi();